import logo from 'assets/icons/logo.png';
import { ROUTE_PATH } from 'constants/routePath';
import { useState } from 'react';
import styles from 'styles/components/common/nav/Nav.module.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron_down.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import SingleBtn from '../elements/SingleBtn';
import { motion, AnimatePresence } from 'framer-motion';
import useAuth from 'hooks/useAuth';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'store/auth';
import { useMutation } from 'react-query';
import { axiosInstance } from 'apis/config/axios';
import { postAllUserLogout } from 'apis/auth';
import useModal from 'hooks/useModal';
import { UserRoleType } from 'types/apis/auth';

const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLogin } = useAuth();
  const { openModal } = useModal();
  const userInfo = useAtomValue(userInfoAtom);

  const [openSubMenu, setOpenSubMenu] = useState<number | null>(null);

  const { mutate: postAllUserLogoutMutation } = useMutation(
    'postAllUserLogout',
    postAllUserLogout,
    {
      onSuccess: () => {
        sessionStorage.clear();
        axiosInstance.defaults.headers.Authorization = '';
        setIsLogin({ isLogin: false });
      },
    }
  );

  const onClickMenu = (routePath: string) => {
    setOpenSubMenu(null);
    navigate(routePath);
  };

  const onClickToggleMenu = (index: number) => {
    setOpenSubMenu(openSubMenu === index ? null : index);
  };

  const onClickMypage = () => {
    navigate(ROUTE_PATH.common.MYPAGE);
  };

  return (
    <>
      <nav className={classNames(styles.nav)}>
        <div className={styles.nav_wrapper}>
          <button onClick={() => onClickMenu(ROUTE_PATH.common.HOME)}>
            <img src={logo} alt="logo" className={styles.logo} />
          </button>

          {userInfo?.userRole === UserRoleType.PROFESSOR ? (
            <div className={styles.grid}>
              <button
                onClick={() => onClickMenu(ROUTE_PATH.professor.STUDENT)}
                className={classNames(styles.menu, {
                  [styles.selected_menu]:
                    location.pathname.includes('/student') ||
                    location.pathname === ROUTE_PATH.common.HOME,
                })}
              >
                학생
              </button>
              <div className={styles.menu_wrapper}>
                <button
                  onClick={() => onClickToggleMenu(1)}
                  className={classNames(styles.menu, {
                    [styles.selected_menu]:
                      location.pathname ===
                        ROUTE_PATH.professor.CLINICAL_PRACTICE ||
                      location.pathname ===
                        ROUTE_PATH.professor.SCORE_MANAGEMENT ||
                      location.pathname ===
                        ROUTE_PATH.professor.REGISTERED_ROUND,
                  })}
                >
                  실습평가
                </button>
                <AnimatePresence>
                  {openSubMenu === 1 && (
                    <>
                      <motion.div
                        initial={{ opacity: 0, height: 'auto' }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 'auto' }}
                        transition={{ duration: 0.1 }}
                        className={styles.sub_menu_wrapper}
                      >
                        <button
                          onClick={() =>
                            onClickMenu(ROUTE_PATH.professor.CLINICAL_PRACTICE)
                          }
                          className={classNames(styles.sub_menu, {
                            [styles.selected_sub_menu]:
                              location.pathname ===
                              ROUTE_PATH.professor.CLINICAL_PRACTICE,
                          })}
                        >
                          실습평가
                        </button>
                        <button
                          onClick={() =>
                            onClickMenu(ROUTE_PATH.professor.SCORE_MANAGEMENT)
                          }
                          className={classNames(styles.sub_menu, {
                            [styles.selected_sub_menu]:
                              location.pathname ===
                              ROUTE_PATH.professor.SCORE_MANAGEMENT,
                          })}
                        >
                          성적관리
                        </button>
                        <button
                          onClick={() =>
                            onClickMenu(ROUTE_PATH.professor.REGISTERED_ROUND)
                          }
                          className={classNames(styles.sub_menu, {
                            [styles.selected_sub_menu]:
                              location.pathname ===
                              ROUTE_PATH.professor.REGISTERED_ROUND,
                          })}
                        >
                          등록 회차
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              <div className={styles.menu_wrapper}>
                <button
                  onClick={() => onClickMenu(ROUTE_PATH.professor.STATISTICS)}
                  className={classNames(styles.menu, {
                    [styles.selected_menu]:
                      location.pathname === ROUTE_PATH.professor.STATISTICS,
                  })}
                >
                  통계
                </button>
              </div>
              <button
                onClick={() => onClickMenu(ROUTE_PATH.common.NOTICE)}
                className={classNames(styles.menu, {
                  [styles.selected_menu]: location.pathname.includes('notice'),
                })}
              >
                공지사항
              </button>
            </div>
          ) : (
            <div className={styles.grid}>
              <button
                onClick={() =>
                  onClickMenu(ROUTE_PATH.student.PATIENT_MANAGEMENT)
                }
                className={classNames(styles.menu, {
                  [styles.selected_menu]:
                    location.pathname ===
                      ROUTE_PATH.student.PATIENT_MANAGEMENT ||
                    location.pathname === ROUTE_PATH.common.HOME,
                })}
              >
                대상자관리
              </button>
              <button
                onClick={() => onClickMenu(ROUTE_PATH.student.SCORE_REPORT)}
                className={classNames(styles.menu, {
                  [styles.selected_menu]:
                    location.pathname === ROUTE_PATH.student.SCORE_REPORT,
                })}
              >
                종합평가
              </button>
              <div className={styles.menu_wrapper}>
                <button
                  onClick={() => onClickToggleMenu(1)}
                  className={classNames(styles.menu, {
                    [styles.selected_menu]:
                      location.pathname === ROUTE_PATH.student.PRACTICE_CHART ||
                      location.pathname === ROUTE_PATH.student.EVALUATION_CHART,
                  })}
                >
                  차트
                </button>
                <AnimatePresence>
                  {openSubMenu === 1 && (
                    <>
                      <motion.div
                        initial={{ opacity: 0, height: 'auto' }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 'auto' }}
                        transition={{ duration: 0.1 }}
                        className={styles.sub_menu_wrapper}
                      >
                        <button
                          onClick={() =>
                            onClickMenu(ROUTE_PATH.student.PRACTICE_CHART)
                          }
                          className={classNames(styles.sub_menu, {
                            [styles.selected_sub_menu]:
                              location.pathname ===
                              ROUTE_PATH.student.PRACTICE_CHART,
                          })}
                        >
                          연습용 차트
                        </button>
                        <button
                          onClick={() =>
                            onClickMenu(ROUTE_PATH.student.EVALUATION_CHART)
                          }
                          className={classNames(styles.sub_menu, {
                            [styles.selected_sub_menu]:
                              location.pathname ===
                              ROUTE_PATH.student.EVALUATION_CHART,
                          })}
                        >
                          실습용 차트
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              <button
                onClick={() => onClickMenu(ROUTE_PATH.common.NOTICE)}
                className={classNames(styles.menu, {
                  [styles.selected_menu]: location.pathname.includes('notice'),
                })}
              >
                공지사항
              </button>
            </div>
          )}
        </div>
        <div className={styles.menu}>
          <div className="flex items_center">
            {userInfo?.userRole === UserRoleType.STUDENT && (
              <SingleBtn
                btnText="+ 대상자 등록"
                isBtnActive={true}
                btnStyle={{
                  height: '38px',
                  padding: '8px 16px',
                  marginRight: '40px',
                  fontSize: '14px',
                }}
                onClick={() => navigate(ROUTE_PATH.student.PATIENT_REGISTER)}
              />
            )}
            <div className="relative">
              <div
                className={styles.mymenu_wrapper}
                onClick={() => onClickToggleMenu(3)}
              >
                <span className={styles.name}>{userInfo?.userName}</span>
                <ChevronDownIcon
                  width={24}
                  height={24}
                  fill={STYLES_VARIABLES.GRAY_08}
                  className="ml8"
                />
              </div>
              <AnimatePresence>
                {openSubMenu === 3 && (
                  <>
                    <motion.div
                      className={styles.sub_mymenu}
                      initial={{ opacity: 0, height: 'auto' }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 'auto' }}
                      transition={{ duration: 0.2 }}
                    >
                      <button
                        className={classNames(styles.sub_menu, {
                          [styles.selected_sub_menu]:
                            location.pathname === ROUTE_PATH.common.MYPAGE ||
                            location.pathname === ROUTE_PATH.common.MYPAGE_EDIT,
                        })}
                        onClick={onClickMypage}
                      >
                        마이페이지
                      </button>
                      <button
                        type="button"
                        className={classNames(styles.sub_menu, {
                          [styles.selected_sub_menu]:
                            location.pathname ===
                            ROUTE_PATH.common.CHANGE_PASSWORD,
                        })}
                        onClick={() =>
                          navigate(ROUTE_PATH.common.CHANGE_PASSWORD)
                        }
                      >
                        비밀번호 변경
                      </button>
                      <button
                        className={styles.sub_menu}
                        onClick={() => {
                          setOpenSubMenu(null);
                          openModal({
                            isConfirm: true,
                            title: '로그아웃',
                            text: '로그아웃하시겠습니까?',
                            onSubmit: () => postAllUserLogoutMutation(),
                          });
                        }}
                      >
                        로그아웃
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
        {openSubMenu && (
          <div
            className={styles.menu_background}
            onClick={() => setOpenSubMenu(null)}
          ></div>
        )}
      </nav>
    </>
  );
};

export default Nav;
